import { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { ChevronDown, Menu, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { AnnouncementBar } from "./HeroSection";
import Logo from "../assets/logo.png"

const routes = [
  { label: "Home", href: "/" },
  { label: "About", href: "/about" },
  { label: "Pricing", href: "/pricing" },
  // {
  //   label: "Resources",
  //   subItems: [
  //     { label: "FAQ", href: "/#faq" },
  //     { label: "Contact Us", href: "/contact" },
  //     { label: "Blog", href: "/blog" },
  //     { label: "Reports & Tools", href: "/report-and-tools" },
  //     { label: "Events", href: "/events" },
  //   ],
  // },
  {
    label: "Products",
    subItems: [
      { label: "SMS", href: "/products/sms" },
      { label: "Email", href: "/products/email" },
      { label: "WhatsApp", href: "/products/whatsapp-messaging" },
      { label: "USSD", href: "/products/ussd" },
    ],
  },
  {
    label: "API Docs",
    href: "/api-docs"
    // href: "https://smarthive-tech-solution.gitbook.io/smarthive-tech-solution-api",
  },
];

const NavBar = () => {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [activeDropdown, setActiveDropdown] = useState<string | null>(null);
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 20);
    };
    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const toggleMenu = () => setOpen(!open);
  const isActive = (href: string) => location.pathname === href;

  return (
    <motion.nav
      initial={{ y: -100 }}
      animate={{ y: 0 }}
      transition={{ duration: 0.6, ease: "easeOut" }}
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
        scrolled
          ? "bg-white/80 backdrop-blur-xl shadow-lg border-b border-primary-100/50"
          : "bg-transparent"
      }`}
    >
      <AnnouncementBar />

      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        <div className="flex items-center justify-between h-16 lg:h-14">
          {/* Logo */}
          <Link to="/" className="flex items-center gap-2 group">
            <div
              className={`w-10 h-10 bg-gradient-to-br ${scrolled ? "from-white to-gray-400 border border-gray-400" : "from-white to-gray-400"} rounded-xl flex items-center justify-center shadow-lg group-hover:shadow-primary-500/30 transition-shadow`}
            >
              {/* <Zap className="w-5 h-5 text-white" /> */}
              <img src={Logo} alt="logo" />
            </div>
            <span
              className={`text-lg font-bold font-poltawski bg-gradient-to-r ${scrolled ? "from-primary-700 to-primary-500" : "from-primary-200 to-primary-100"} bg-clip-text text-transparent`}
            >
              SmarthiveSMS
            </span>
          </Link>

          {/* Desktop Nav */}
          <div className="hidden lg:flex items-center gap-1">
            {routes.map((route) =>
              route.subItems ? (
                <div
                  key={route.label}
                  className="relative"
                  onMouseEnter={() => setActiveDropdown(route.label)}
                  onMouseLeave={() => setActiveDropdown(null)}
                >
                  <button
                    className={`flex items-center gap-1 px-4 py-2 text-sm font-medium rounded-lg transition-all ${
                      activeDropdown === route.label
                        ? "text-primary-600 bg-primary-50"
                        : scrolled
                          ? `text-gray-700 hover:text-primary-600 hover:bg-gray-50`
                          : `text-gray-300 hover:text-primary-600 hover:bg-gray-50`
                    }`}
                  >
                    {route.label}
                    <ChevronDown
                      size={14}
                      className={`transition-transform duration-200 ${
                        activeDropdown === route.label ? "rotate-180" : ""
                      }`}
                    />
                  </button>

                  <AnimatePresence>
                    {activeDropdown === route.label && (
                      <motion.div
                        initial={{ opacity: 0, y: 8, scale: 0.95 }}
                        animate={{ opacity: 1, y: 0, scale: 1 }}
                        exit={{ opacity: 0, y: 8, scale: 0.95 }}
                        transition={{ duration: 0.15 }}
                        className="absolute top-full left-0 mt-2 w-56 bg-white rounded-2xl shadow-xl border border-gray-100 overflow-hidden"
                      >
                        {route.subItems.map((subItem) => (
                          <Link
                            key={subItem.label}
                            to={subItem.href}
                            className="block px-4 py-3 text-sm text-gray-600 hover:text-primary-600 hover:bg-primary-50/50 transition-colors first:pt-4 last:pb-4"
                            onClick={() => setActiveDropdown(null)}
                          >
                            {subItem.label}
                          </Link>
                        ))}
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>
              ) : (
                <Link
                  key={route.label}
                  to={route.href}
                  className={`px-4 py-2 text-sm font-medium rounded-lg transition-all ${
                    isActive(route.href)
                      ? "text-primary-600 bg-primary-50"
                      : scrolled
                        ? `text-gray-700 hover:text-primary-600 hover:bg-gray-50`
                        : `text-gray-300 hover:text-primary-600 hover:bg-gray-50`
                  }`}
                >
                  {route.label}
                </Link>
              ),
            )}
          </div>

          {/* CTA */}
          <div className="hidden lg:flex items-center gap-3">
            <a
              href="https://app.smarthivesms.com/SignIn"
              target="_blank"
              rel="noopener noreferrer"
              className={`${scrolled? "text-gray-700":"text-gray-200"} text-sm font-medium hover:text-primary-600 transition-colors px-4 py-2`}
            >
              Log in
            </a>
            <a
              href="https://app.smarthivesms.com/signup"
              target="_blank"
              rel="noopener noreferrer"
            >
              <Button className="bg-primary-600 hover:bg-primary-700 text-white px-6 py-2.5 rounded-xl shadow-lg shadow-primary-500/25 hover:shadow-primary-500/40 transition-all hover:-translate-y-0.5 text-sm font-semibold">
                Get Started
              </Button>
            </a>
          </div>

          {/* Mobile Toggle */}
          <button
            className={`${scrolled? "text-gray-600":"text-gray-100"} lg:hidden p-2 hover:text-primary-600 transition-colors`}
            onClick={toggleMenu}
            aria-label="Toggle menu"
          >
            {open ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>
      </div>

      {/* Mobile Menu */}
      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.3 }}
            className="lg:hidden bg-white/95 backdrop-blur-xl border-t border-gray-100 overflow-hidden"
          >
            <div className="px-6 py-4 space-y-1">
              {routes.map((route) =>
                route.subItems ? (
                  <div key={route.label} className="py-2">
                    <span className="text-xs font-semibold text-gray-400 uppercase tracking-wider px-3">
                      {route.label}
                    </span>
                    <div className="mt-1 space-y-0.5">
                      {route.subItems.map((sub) => (
                        <Link
                          key={sub.label}
                          to={sub.href}
                          onClick={toggleMenu}
                          className="block px-3 py-2.5 text-sm text-gray-600 hover:text-primary-600 hover:bg-primary-50 rounded-lg transition-colors"
                        >
                          {sub.label}
                        </Link>
                      ))}
                    </div>
                  </div>
                ) : (
                  <Link
                    key={route.label}
                    to={route.href}
                    onClick={toggleMenu}
                    className={`block px-3 py-2.5 text-sm font-medium rounded-lg transition-colors ${
                      isActive(route.href)
                        ? "text-primary-600 bg-primary-50"
                        : "text-gray-600 hover:text-primary-600 hover:bg-gray-50"
                    }`}
                  >
                    {route.label}
                  </Link>
                ),
              )}
              <div className="pt-4 border-t border-gray-100 space-y-2">
                <a
                  href="https://app.smarthivesms.com/SignIn"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="block w-full text-center py-3 text-sm font-medium text-gray-600 hover:text-primary-600 transition-colors"
                >
                  Log in
                </a>
                <a
                  href="https://app.smarthivesms.com/signup"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="block w-full"
                >
                  <Button className="w-full bg-primary-600 hover:bg-primary-700 text-white py-3 rounded-xl font-semibold">
                    Get Started
                  </Button>
                </a>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.nav>
  );
};

export default NavBar;
